import { useCallback, useEffect, useRef, useState } from "react";

import { apiClient, type CompanyChecklist } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { standardChannelsNotice } from "@/features/settings/standard-channels";

/**
 * A lista "Configuração da empresa" carregada do servidor, UMA vez ao
 * montar (sem polling) e de novo a pedido (`reload`).
 *
 * Falha de carga NUNCA derruba a tela que hospeda o card: vira `error`, que
 * o card mostra com "tentar de novo". O descarte é por CARGA (contador em
 * `useRef`): uma carga nova invalida a anterior, e a resposta velha que
 * chega depois não sobrescreve a nova.
 *
 * `createStandardChannels` é a ação de um clique do item de canais: chama a
 * rota que a aba Canais já usa, recarrega a lista e devolve a frase do que
 * aconteceu (a recarga vem ANTES do aviso, a regra de 2026-10-09).
 */
export function useCompanyChecklist(token: string, enabled = true) {
  const [checklist, setChecklist] = useState<CompanyChecklist | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const loadId = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++loadId.current;
    setLoading(true);
    try {
      const result = await apiClient.companyChecklist(token);
      // Corpo que não é a lista (proxy, versão velha da API) é falha de carga,
      // não motivo pra derrubar a tela.
      if (!Array.isArray(result?.items)) throw new Error("resposta inesperada");
      if (mine !== loadId.current) return;
      setChecklist(result);
      setError(null);
    } catch (failure) {
      if (mine !== loadId.current) return;
      setError(errorMessage(failure, "Tente de novo em instantes."));
    } finally {
      if (mine === loadId.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const createStandardChannels = useCallback(async (): Promise<string> => {
    const result = await apiClient.createStandardNetworkChannels(token);
    await reload();
    return standardChannelsNotice(result);
  }, [token, reload]);

  return { checklist, loading, error, reload, createStandardChannels };
}
